import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import logo from '../assets/logo.svg';

const CONTENT = {
  fr: {
    nav: { lang: "EN" },
    title: "Politique de confidentialité",
    lastUpdated: "Dernière mise à jour : mars 2026",
    highlight: {
      title: "Ce que nous collectons",
      value: "Rien.",
      desc: "VOYX fonctionne entièrement sur ton appareil. Aucune donnée vocale, aucune métrique, aucun enregistrement ne quitte ton iPhone. Jamais."
    },
    permissions: {
      title: "Autorisations demandées",
      desc: "VOYX demande quatre autorisations système. Chacune reste locale.",
      items: [
        { name: "Microphone", reason: "Analyse vocale en temps réel (fréquence, volume, résonance). L'audio est traité en mémoire et jamais stocké sur un serveur." },
        { name: "Caméra", reason: "Enregistrement vidéo en mode Capture. La vidéo reste sur l'appareil jusqu'à l'export dans ta Photothèque." },
        { name: "Reconnaissance vocale", reason: "Calcul du débit de parole via SFSpeechRecognizer (on-device). Aucune transcription n'est envoyée à Apple ou à un tiers." },
        { name: "Photothèque", reason: "Sauvegarde des vidéos exportées. VOYX n'accède qu'en écriture, jamais en lecture de tes photos existantes." }
      ]
    },
    sections: [
      {
        title: "Tiers et analytics",
        content: "Aucun. Pas de Firebase Analytics, pas de SDK tiers, pas de pixels de tracking. VOYX ne contient aucun code qui envoie des données à l'extérieur de ton appareil."
      },
      {
        title: "Stockage des données",
        content: "Tous les enregistrements (audio M4A, métriques VXFR) sont stockés dans le sandbox de l'application sur ton appareil. Les vidéos composées sont sauvegardées dans ta Photothèque puis supprimées du sandbox. Supprimer l'app = supprimer toutes les données."
      },
      {
        title: "Transfert de données",
        content: "VOYX ne transmet aucune donnée sur Internet. L'application fonctionne intégralement hors ligne. Aucun compte utilisateur n'est requis."
      },
      {
        title: "Enfants",
        content: "VOYX ne collecte aucune donnée personnelle, y compris celle des enfants de moins de 13 ans. L'application est conforme au COPPA par conception."
      },
      {
        title: "Modifications",
        content: "Si cette politique change, la date de mise à jour sera modifiée en haut de cette page. Étant donné que nous ne collectons aucune donnée, il n'y a rien à notifier."
      },
      {
        title: "Contact",
        content: "Pour toute question concernant cette politique :"
      }
    ],
    contact: "privacy@voyx.app",
    backHome: "Retour à l'accueil",
    footer: "© 2026 VOYX. Tous droits réservés."
  },
  en: {
    nav: { lang: "FR" },
    title: "Privacy Policy",
    lastUpdated: "Last updated: March 2026",
    highlight: {
      title: "What we collect",
      value: "Nothing.",
      desc: "VOYX runs entirely on your device. No voice data, no metrics, no recordings ever leave your iPhone. Ever."
    },
    permissions: {
      title: "Permissions requested",
      desc: "VOYX requests four system permissions. Each stays local.",
      items: [
        { name: "Microphone", reason: "Real-time voice analysis (frequency, volume, resonance). Audio is processed in memory and never stored on any server." },
        { name: "Camera", reason: "Video recording in Capture mode. Video stays on device until exported to your Photo Library." },
        { name: "Speech Recognition", reason: "Speech rate calculation via SFSpeechRecognizer (on-device). No transcription is sent to Apple or any third party." },
        { name: "Photo Library", reason: "Saving exported videos. VOYX only writes. It never reads your existing photos." }
      ]
    },
    sections: [
      {
        title: "Third parties & analytics",
        content: "None. No Firebase Analytics, no third-party SDKs, no tracking pixels. VOYX contains zero code that sends data outside your device."
      },
      {
        title: "Data storage",
        content: "All recordings (M4A audio, VXFR metrics) are stored in the app's sandbox on your device. Composited videos are saved to your Photo Library then deleted from the sandbox. Delete the app = delete all data."
      },
      {
        title: "Data transfer",
        content: "VOYX transmits no data over the Internet. The app works entirely offline. No user account is required."
      },
      {
        title: "Children",
        content: "VOYX does not collect any personal data, including from children under 13. The app is COPPA-compliant by design."
      },
      {
        title: "Changes",
        content: "If this policy changes, the date at the top of this page will be updated. Since we collect no data, there is nothing to notify about."
      },
      {
        title: "Contact",
        content: "For any questions about this policy:"
      }
    ],
    contact: "privacy@voyx.app",
    backHome: "Back to Home",
    footer: "© 2026 VOYX. All rights reserved."
  }
};

const MicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>
  </svg>
);

const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z"/>
    <circle cx="12" cy="13" r="3"/>
  </svg>
);

const SpeechIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 6V2"/><path d="M7 10V6"/><path d="M17 10V6"/><path d="M2 14v-2"/><path d="M22 14v-2"/>
    <path d="M7 14v4"/><path d="M12 14v6"/><path d="M17 14v4"/>
    <path d="M2 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2"/>
    <path d="M2 10a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2"/>
  </svg>
);

const PhotoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/>
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
  </svg>
);

const PERMISSION_ICONS = [<MicIcon />, <CameraIcon />, <SpeechIcon />, <PhotoIcon />];

const Privacy = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('voyx-lang') || 'fr');
  const t = CONTENT[lang];
  const toggleLang = () => setLang(prev => { const next = prev === 'fr' ? 'en' : 'fr'; localStorage.setItem('voyx-lang', next); return next; });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="landing-page">
      {/* ─── Navbar ─── */}
      <nav>
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src={logo} alt="VOYX" />
          </div>
          <div className="nav-links" />
          <div className="nav-right">
            <button className="nav-lang" onClick={toggleLang}>{t.nav.lang}</button>
            <button className="nav-cta" onClick={() => navigate('/login')}>
              {lang === 'fr' ? "Voir l'invisible" : "See the invisible"}
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Privacy Content ─── */}
      <div className="privacy-page">
        <div className="container" style={{ maxWidth: '800px' }}>

          <h1>{t.title}</h1>
          <p className="privacy-updated">{t.lastUpdated}</p>

          {/* Hero card: What we collect = Nothing */}
          <div className="privacy-highlight">
            <span className="privacy-highlight__label">{t.highlight.title}</span>
            <span className="privacy-highlight__value">{t.highlight.value}</span>
            <p className="privacy-highlight__desc">{t.highlight.desc}</p>
          </div>

          {/* Permissions grid */}
          <div className="privacy-section">
            <h2>{t.permissions.title}</h2>
            <p>{t.permissions.desc}</p>
            <div className="privacy-permissions">
              {t.permissions.items.map((item, i) => (
                <div key={i} className="privacy-permission-card">
                  <span className="privacy-permission-card__icon">{PERMISSION_ICONS[i]}</span>
                  <h3>{item.name}</h3>
                  <p>{item.reason}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Text sections */}
          {t.sections.map((section, i) => (
            <div key={i} className="privacy-section">
              <h2>{section.title}</h2>
              <p>{section.content}</p>
              {section.title === 'Contact' && (
                <a href={`mailto:${t.contact}`} className="privacy-email">{t.contact}</a>
              )}
            </div>
          ))}

          <div style={{ textAlign: 'center', marginTop: '60px', marginBottom: '20px' }}>
            <button className="hero-cta" onClick={() => navigate('/')}>
              {t.backHome}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Footer ─── */}
      <footer>
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src={logo} alt="VOYX" onClick={() => navigate('/')} />
            <span>{t.footer}</span>
          </div>
          <div className="footer-links">
            <a onClick={() => navigate('/')}>
              {lang === 'fr' ? 'Accueil' : 'Home'}
            </a>
            <a onClick={() => navigate('/about')}>
              {lang === 'fr' ? 'À propos' : 'About'}
            </a>
            <a onClick={() => navigate('/contact')}>
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
